import { queryClient } from '@/utils/reactQueryClient';
import { gettingStartedService } from './getting-started.service';
import {
	createCompaniesController,
	createCompanyController,
	createCompanyPublicController,
	createRequestFn,
	type CompanyResponse,
	type SDKResult,
} from '@saas/api-client';
import { useMutation } from '@tanstack/react-query';
import ga4 from '@/lib/ga4';
import { globalAuthRequestFunction, publicRequestFunction } from '../http/request';
import { makeMutation, makeQuery } from '../http/queryUtils';
import { invalidatePermissionQueries } from './permissions.service';

// ---------------------------------------------------------------------------
// Controllers that exist in the SDK
// ---------------------------------------------------------------------------
const companyPublicController = createCompanyPublicController(publicRequestFunction);
const companyController = createCompanyController(globalAuthRequestFunction);
const companiesController = createCompaniesController(globalAuthRequestFunction);

// --- Keys factory ---

export const companyServiceKeys = {
	base: 'COMPANY',
	branding: (params: Parameters<typeof companyPublicController.getBranding>[0]) =>
		[companyServiceKeys.base, 'BRANDING', params] as const,
	meetingCapability: (params: Parameters<typeof companyPublicController.getMeetingCapability>[0]) =>
		[companyServiceKeys.base, 'MEETING_CAPABILITY', params] as const,
	companiesAuth: () => [companyServiceKeys.base, 'COMPANIES_AUTH'] as const,
	company: () => [companyServiceKeys.base, 'COMPANY'] as const,
	memberOrderMetrics: (params: Parameters<typeof companyController.getMemberOrderMetrics>[0]) =>
		[companyServiceKeys.base, 'MEMBER_ORDER_METRICS', params] as const,
	registrySearch: (params: Parameters<typeof companiesController.searchRegistry>[0]) =>
		[companyServiceKeys.base, 'REGISTRY_SEARCH', params] as const,
};

// --- Invalidation map ---

export const invalidateCompanyQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [companyServiceKeys.base] }),
	companiesAuth: () => queryClient.invalidateQueries({ queryKey: companyServiceKeys.companiesAuth() }),
	company: () => queryClient.invalidateQueries({ queryKey: companyServiceKeys.company() }),
	memberOrderMetrics: () =>
		queryClient.invalidateQueries({ queryKey: [companyServiceKeys.base, 'MEMBER_ORDER_METRICS'] }),
};

// --- Queries ---

const useGetWhitelabelingByDomain = makeQuery({
	queryFunction: companyPublicController.getBranding,
	queryKeyFactory: (params) => [...companyServiceKeys.branding(params)],
});

const useGetPublicMeetingCapability = makeQuery({
	queryFunction: companyPublicController.getMeetingCapability,
	queryKeyFactory: (params) => [...companyServiceKeys.meetingCapability(params)],
});

// Hand-rolled hook: uses useMutation with custom token passing
const useGetCompanies = () => {
	return useMutation({
		mutationFn: async (tokens: { token: string }) => {
			const requestFn = createRequestFn({
				baseUrl: import.meta.env.VITE_API_URL,
				async onRequest(_url, init) {
					const headers = new Headers(init.headers);
					headers.set('Authorization', `Bearer ${tokens.token}`);
					return { ...init, headers };
				},
			});
			const result: SDKResult<CompanyResponse[]> = await requestFn('GET', '/companies');
			if (result.error) throw new Error(result.error.message);
			return result.data;
		},
	});
};

const useGetCompaniesAuth = makeQuery({
	queryFunction: companiesController.getCompanies,
	queryKeyFactory: () => [...companyServiceKeys.companiesAuth()],
});

const useGetCompany = makeQuery({
	queryFunction: companyController.getCompany,
	queryKeyFactory: () => [...companyServiceKeys.company()],
});

// The Brønnøysund typeahead behind the signup company form. User-level, so it works before the
// person owns a workspace; the CRM has its own tenant-scoped copy of the same search.
const useSearchCompanyRegistry = makeQuery({
	queryFunction: companiesController.searchRegistry,
	queryKeyFactory: (params) => [...companyServiceKeys.registrySearch(params)],
});

const useGetMemberOrderMetrics = makeQuery({
	queryFunction: companyController.getMemberOrderMetrics,
	queryKeyFactory: (params) => [...companyServiceKeys.memberOrderMetrics(params)],
});

// --- Mutations ---

const useCheckCompanyDomain = makeMutation({
	mutationFunction: companiesController.checkAvailability,
});

/** Whether a sub-domain is still free, as a plain call for code that tries several in a row. */
const isSubdomainAvailable = async (subDomain: string): Promise<boolean> => {
	const result = await companiesController.checkAvailability({ subDomain });
	return result.error ? false : result.data === true;
};

const useLookupRegistryIndustry = makeMutation({
	mutationFunction: companiesController.lookupRegistryIndustry,
});

const useCreateCompany = makeMutation({
	mutationFunction: companiesController.createCompany,
	onSuccess: (_variables, data) => {
		ga4.event('company_created', {
			company_id: data.id,
			company_name: data.name,
			company_subdomain: data.subDomain,
			company_whitelabeling_domain: data.whiteLabeling?.customDomain,
			plan: data?.companyBillingEntity?.plan,
		});
		invalidateCompanyQueries.companiesAuth();
	},
});

const useInvitesUser = makeMutation({
	mutationFunction: companyController.inviteMembers,
	onSuccess: () => {
		invalidateCompanyQueries.company();
		gettingStartedService.invalidate.all();
	},
});

const useRemovePendingUser = makeMutation({
	mutationFunction: companyController.deleteInvitation,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useRemoveUserFromCompany = makeMutation({
	mutationFunction: companyController.removeCompanyUser,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useUpdateCompanyUserRole = makeMutation({
	mutationFunction: companyController.changeRole,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useAssignRole = makeMutation({
	mutationFunction: companyController.assignRole,
	onSuccess: () => {
		invalidateCompanyQueries.company();
		invalidatePermissionQueries.myEffectivePermissions();
	},
});

const useDeleteCompany = makeMutation({
	mutationFunction: companyController.deleteCompany,
	onSuccess: () => {
		invalidateCompanyQueries.companiesAuth();
	},
});

const useUpdateCompanyAvatar = makeMutation({
	mutationFunction: companyController.updateLogo,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useUpdateCompanyBanner = makeMutation({
	mutationFunction: companyController.updateBanner,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useUpdateCompanyProfile = makeMutation({
	mutationFunction: companyController.updateCompany,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useUpdateAiTranscriptionLang = makeMutation({
	mutationFunction: companyController.updateAiSummaryTranscriptionLang,
	onSuccess: () => {
		invalidateCompanyQueries.company();
	},
});

const useUpdateMeetingPolicy = makeMutation({
	mutationFunction: companyController.updateMeetingPolicy,
});

const useUpdateCompanyUserFlags = makeMutation({
	mutationFunction: companyController.updateCompanyUserFlags,
	onSuccess: () => {
		invalidateCompanyQueries.all();
	},
});

// --- Export ---

export const companyService = {
	useGetCompanies,
	useCheckCompanyDomain,
	isSubdomainAvailable,
	useInvitesUser,
	useCreateCompany,
	useGetCompaniesAuth,
	useGetCompany,
	useGetMemberOrderMetrics,
	useSearchCompanyRegistry,
	useLookupRegistryIndustry,
	useRemovePendingUser,
	useDeleteCompany,
	useRemoveUserFromCompany,
	useUpdateCompanyUserRole,
	useAssignRole,
	useUpdateCompanyProfile,
	useUpdateCompanyAvatar,
	useUpdateCompanyBanner,
	useGetWhitelabelingByDomain,
	useGetPublicMeetingCapability,
	useUpdateAiTranscriptionLang,
	useUpdateCompanyUserFlags,
	useUpdateMeetingPolicy,
};
